import {
    addRows,
    arrange,
    distinct,
    fill,
    filter,
    fixedOrder,
    Granularity,
    groupBy,
    mean,
    mutate,
    nDistinct,
    pivotLonger,
    rename,
    select,
    sum,
    summarize,
    tidy,
    vectorSeqDate,
} from "@tidyjs/tidy";
import { addDays, parseISO } from "date-fns";
import subMonths from "date-fns/subMonths";

import { defined } from "@formant/core/common/defined";
import { AnalyticsAggregateLevel } from "@formant/core/model/AnalyticsAggregateLevel";
import { IEventColumn } from "@formant/core/model/IEventColumn";
import { ISqlColumn } from "@formant/core/model/ISqlColumn";
import { ISqlQuery } from "@formant/core/model/ISqlQuery";
import { ISqlRow } from "@formant/core/model/ISqlRow";
import { IStreamColumn } from "@formant/core/model/IStreamColumn";
import { ITableColumn } from "@formant/core/model/ITableColumn";
import { ITaskReportColumn } from "@formant/core/model/ITaskReportColumn";

import { deviceStore } from "../../stores";
import { formatDateString } from "../../utils/formatDateString";
import { ISummaryValue } from "./AnalyticsSummaryChart";
import { ISeries } from "./Chart";
import { IPieData } from "./PieChart";

export interface IChartOptions {
    xAxis: ISqlColumn;
    partitionBy?: string;
    yAxis: string[];
}

// used to identify the origin of the data
export interface IOrigin {
    ORIGIN_TYPE?: ITableColumn["type"] | "advanced";
    ORIGIN_ID?: string;
    HEADER?: string;
    ORIGIN?: ITableColumn;
}

export interface IPoint extends IOrigin {
    x: string;
    y: number;
    DEVICE_ID?: string;
    UNIT?: string;
    GROUP_NAME: string;
}

// const series = new ChartBuilder({
//     start: query.start,
//     end: query.end,
//     aggregateLevel: "day",
//     fillGaps: true,
// }, columns, rows){
//     xAxis: { dataType: "timestamp", name: "time", tableName: "battery" },
//     yAxis: ["Percentage", "Charge", "Voltage"],
// }).toSeries();

export class ChartBuilder {
    private query: ISqlQuery;
    public options: IChartOptions;
    public points: IPoint[] = [];
    private rows: ISqlRow[] = [];
    private columns: ISqlColumn[] | undefined;
    private isTimeSeries: boolean | undefined;

    constructor(query: ISqlQuery, columns: ISqlColumn[], rows: ISqlRow[]) {
        this.query = query;
        this.rows = rows;
        this.columns = columns;
        this.options = this.getRecommendedChartOptions(columns);
        this.points = this.init();
    }

    init(): IPoint[] {
        const { xAxis, partitionBy } = this.options;
        this.isTimeSeries =
            this.options.xAxis.dataType === "timestamp" ||
            this.options.xAxis.name.toLowerCase() === "time";

        const result = tidy(
            this.fillAllOrigin(this.rows),
            mutate({
                x: d => String(d[xAxis.name]),
            }),
            rename({
                unit: "UNIT",
                device_id: "DEVICE_ID",
                deviceId: "DEVICE_ID",
                key: "LABEL",
                label: "LABEL",
            }),
            pivotLonger({
                cols: this.options.yAxis,
                namesTo: "GROUP_NAME",
                valuesTo: "y",
            }),
            mutate({
                x: d =>
                    xAxis.name.toLowerCase() === "device_id"
                        ? deviceIdToDeviceName(String(d.x), d.DEVICE_ID)
                        : d.x,
            }),
            mutate({
                DEVICE_ID: d => deviceIdToDeviceName(d.DEVICE_ID, ""),
            }),
            filter(
                d =>
                    d.x !== "" &&
                    d.x !== null &&
                    d.x !== undefined &&
                    d.y !== null &&
                    d.y !== undefined,
            ),
            mutate({
                x: d => String(d.x),
                y: d => parseFloat(d.y || 0),
                GROUP_NAME: d => {
                    if (partitionBy) {
                        if (partitionBy === "unset") {
                            return d.GROUP_NAME || "";
                        }
                        if (partitionBy?.toUpperCase() in d) {
                            return d?.[partitionBy?.toUpperCase()];
                        }
                        if (partitionBy in d) {
                            return d?.[partitionBy];
                        }
                    }
                    return d.LABEL !== undefined ? d.LABEL : d.GROUP_NAME || "";
                },
            }),
        );

        return result;
    }

    setOptions(options: IChartOptions): this {
        this.options = options;
        this.points = this.init();
        return this;
    }

    setXAxis(name: string | undefined): this {
        if (!name) {
            return this;
        }

        const validColumn = this.columns?.find(_ => _.name === name);
        const isValid = this.availableXOptions(this.columns || []).includes(
            name,
        );
        if (isValid && validColumn) {
            this.options.xAxis = validColumn;
            this.points = this.init();
        }
        return this;
    }

    setYAxis(columns: string[]): this {
        const validColumns = this.columns?.map(_ => _.name) || [];
        this.options.yAxis = columns.filter(_ => validColumns.includes(_));
        this.points = this.init();
        return this;
    }

    partitionByDefault(): this {
        return this.partitionBy();
    }

    setPartitionBy(column: string): this {
        const valid =
            this.columns?.find(_ => column === _.name) || column === "unset";
        if (valid) {
            this.options.partitionBy = column;
            this.points = this.init();
        }
        return this;
    }

    partitionBy(): this {
        if (this.points === undefined) {
            return this;
        }

        this.partitionByDeviceId();

        const filteredPoints = tidy(
            this.points,
            filter(d => d.y !== null && d.y !== undefined),
            groupBy(
                "GROUP_NAME",
                summarize({
                    GROUP_NAME_COUNT: nDistinct("HEADER"),
                    items: items => items,
                }),
            ),
            mutate({
                items: a =>
                    a.items.map(item => ({
                        ...item,
                        GROUP_NAME:
                            a.GROUP_NAME_COUNT > 1
                                ? [item.GROUP_NAME, item.HEADER].join(" - ")
                                : item.GROUP_NAME,
                    })),
            }),
        ).flatMap(_ => _.items);

        this.points = filteredPoints;
        return this;
    }

    fillAllOrigin(rows: ISqlRow[]): ISqlRow[] {
        if (this.query?.sqlQuery) {
            return tidy(
                rows,
                mutate({
                    ORIGIN_TYPE: "advanced",
                    ORIGIN_ID: "advanced",
                    HEADER: "",
                }),
            );
        }

        const names = (this.query?.tableColumns || []).reduce(
            (acc, item) => {
                acc[toTableColumnId(item)] = item;
                return acc;
            },
            {} as Record<string, ITableColumn>,
        );

        const findKeys = (d: ISqlRow, keys: string[]): string | undefined =>
            keys
                .map(key =>
                    key in d || (key.toUpperCase() in d && d[key.toUpperCase()])
                        ? String(d[key in d ? key : key.toUpperCase()])
                        : undefined,
                )
                .find(_ => _ !== undefined);

        return tidy(
            rows,
            mutate({
                ORIGIN_ID: (d: ISqlRow): string =>
                    toOriginId(
                        findKeys(d, ["type", "stream_type"]),
                        findKeys(d, ["stream_name", "name"]),
                        findKeys(d, ["tableName"]),
                    ),
                HEADER: (d: ISqlRow) =>
                    findKeys(d, ["stream_name", "name", "type"]),
                ORIGIN_TYPE: (d: ISqlRow) => names[String(d.ORIGIN_ID)]?.type,
                ORIGIN: (d: ISqlRow) => names[String(d.ORIGIN_ID)],
            }),
        );
    }

    getSeriesNames(): string[] {
        if (this.points === undefined) {
            return [];
        }

        const distinctLabels = tidy(
            this.points,
            distinct(["GROUP_NAME"]),
            select(["GROUP_NAME"]),
        );

        return distinctLabels.map(_ => _.GROUP_NAME).filter(_ => _);
    }

    getSeriesNamesPartitioned(): {
        ORIGIN_ID?: string;
        ORIGIN?: ITableColumn<"event" | "stream" | "task-report">;
        names: string[];
    }[] {
        return tidy(
            this.points,
            groupBy(
                ["ORIGIN_ID", "ORIGIN"],
                summarize({
                    names: items => [...new Set(items.map(_ => _.GROUP_NAME))],
                }),
            ),
        );
    }

    partitionByDeviceId(): this {
        if (
            !(
                this.query.groupByDevice &&
                this.options.xAxis.name !== "DEVICE_ID" &&
                this.columns?.find(c => c.name === "DEVICE_ID")
            )
        ) {
            return this;
        }

        this.points = tidy(
            this.points,
            mutate({
                GROUP_NAME: d =>
                    d.DEVICE_ID
                        ? [d.DEVICE_ID, d.GROUP_NAME].join(" - ")
                        : d.GROUP_NAME,
            }),
        );
        return this;
    }

    findColumnByType(
        columns: ISqlColumn[],
        dataType: ISqlColumn["dataType"],
    ): ISqlColumn | undefined {
        return columns.find(column => column.dataType === dataType);
    }

    findColumnByName(
        columns: ISqlColumn[],
        name?: string,
    ): ISqlColumn | undefined {
        return columns.find(column => column.name === name);
    }

    findColumnByTypeOrName(
        columns: ISqlColumn[],
        dataType: ISqlColumn["dataType"],
        name: string,
    ): ISqlColumn | undefined {
        return (
            this.findColumnByType(columns, dataType) ||
            this.findColumnByName(columns, name)
        );
    }

    findColumn(
        columns: ISqlColumn[] | undefined,
        type: ISqlColumn["dataType"],
        name?: string,
    ): ISqlColumn | undefined {
        return columns?.find(
            col =>
                col.dataType === type ||
                (name && col.name.toLowerCase() === name.toLowerCase()),
        );
    }

    mapRawRowToColumn(
        type: ISqlColumn["dataType"],
        rows: ISqlRow[],
        column?: ISqlColumn,
    ): [string, number][] {
        if (rows.length === 0 || !column || column.dataType === "null") {
            return [];
        }
        const { name, dataType } = column;

        if (type === dataType && dataType === "number" && name) {
            return rows.map(row => [name, parseFloat(String(row[name])) || 0]);
        }
        return [];
    }

    toSeries(): ISeries[] {
        if (this.points === undefined) {
            return [];
        }

        const xAxis = this.getXAxis();

        const groupAggregatesOnFrontend = ["day", "hour"].includes(
            this.query.aggregateLevel || "",
        );

        const groupedData = tidy(
            this.isTimeSeries
                ? this.fillDates(this.points as IPoint[])
                : this.points,
            groupBy(
                ["x", "GROUP_NAME", "ORIGIN_ID", "UNIT"],
                summarize({
                    y: groupAggregatesOnFrontend ? mean("y") : sum("y"),
                }),
            ),
            groupBy(
                ["GROUP_NAME", "ORIGIN_ID"],
                summarize({ items: items => items }),
            ),
            select(["GROUP_NAME", "ORIGIN_ID", "items"]),
        );

        type GroupedItem = {
            GROUP_NAME: string;
            ORIGIN_ID?: string;
            items: { x: string; y: number | undefined }[];
        };

        const result = (groupedData as unknown as GroupedItem[]).map(
            ({ GROUP_NAME: label, ORIGIN_ID: originId, items }) => ({
                label,
                originId,
                values: xAxis.map(
                    xValue => items.find(({ x }) => x === xValue)?.y ?? 0,
                ),
            }),
        );
        return result;
    }

    getXAxis(): string[] {
        const startingPoints = this.isTimeSeries
            ? this.fillDates(this.points as IPoint[])
            : this.points;
        return tidy(startingPoints, distinct("x")).map(_ => _.x);
    }

    fillDates(points: IPoint[]): IPoint[] {
        const { start, end, aggregateLevel, fillGaps } = this.query;
        const [granularity, period] = toGranularity(aggregateLevel || "day");
        if (!points) {
            return points || [];
        }

        const clampToStartOfDay = (date: Date): Date => {
            date.setHours(0, 0, 0, 0);
            return date;
        };

        const clampToEndOfDay = (date: Date): Date => {
            date.setHours(23, 59, 59, 999);
            return date;
        };

        const startDate = clampToStartOfDay(
            start ? new Date(start) : subMonths(new Date(), 1),
        );
        const endDate = clampToEndOfDay(end ? new Date(end) : new Date());
        const fullRows = !fillGaps
            ? ([] as Date[])
            : vectorSeqDate([startDate, endDate], granularity, period);

        const p = tidy(
            points,
            addRows(
                fullRows.map(
                    _ =>
                        ({
                            x: _.toISOString(),
                            y: 0,
                        }) as unknown as IPoint,
                ),
            ),
            fill(["DEVICE_ID", "GROUP_NAME"]),
            arrange(
                (a, b) => new Date(a.x).getTime() - new Date(b.x).getTime(),
            ),
        );

        return this.formatDateTicks(p);
    }

    formatDateTicks(points: IPoint[]): IPoint[] {
        const { aggregateLevel } = this.query;

        if (!points || !aggregateLevel) {
            return points || [];
        }

        const formatDateSafe = (date: string) => {
            try {
                return new Date(date).toISOString();
            } catch {
                return new Date(date.replace(/ /g, "T")).toISOString();
            }
        };

        const tidyRows: IPoint[] = tidy(
            points,
            mutate({
                x: (d: { x: string }) =>
                    formatTicks(formatDateSafe(d.x), aggregateLevel),
            }),
        );

        if (aggregateLevel === "day") {
            return tidy(
                tidyRows,
                arrange([
                    fixedOrder("x", [
                        "Monday",
                        "Tuesday",
                        "Wednesday",
                        "Thursday",
                        "Friday",
                        "Saturday",
                        "Sunday",
                    ]),
                ]),
            );
        } else if (aggregateLevel === "month") {
            return tidy(
                tidyRows,
                arrange([
                    fixedOrder("x", [
                        "Jan",
                        "Feb",
                        "Mar",
                        "Apr",
                        "May",
                        "Jun",
                        "Jul",
                        "Aug",
                        "Sep",
                        "Oct",
                        "Nov",
                        "Dec",
                    ]),
                ]),
            );
        } else if (aggregateLevel === "hour") {
            const getHoursInOrder = (): string[] => {
                const hours: string[] = [];
                for (let i = 0; i < 24; i++) {
                    const hour = i % 12 === 0 ? 12 : i % 12;
                    const period = i < 12 ? "AM" : "PM";
                    hours.push(`${hour}:00 ${period}`);
                }
                return hours;
            };
            return tidy(
                tidyRows,
                arrange([fixedOrder("x", getHoursInOrder())]),
            );
        }
        return tidyRows;
    }

    toPieSeries = (): IPieData[] => {
        if (this.options.partitionBy) {
            return this.points
                .filter(_ => _.GROUP_NAME)
                .map(_ => ({
                    name: String(_.GROUP_NAME),
                    value: _.y ?? 1,
                }));
        }
        return this.points.map(_ => ({
            name: String(_.GROUP_NAME || _.x),
            value: _.y ?? 1,
        }));
    };

    toSummary = (): ISummaryValue[] => {
        if (this.options.yAxis.length === 0) {
            return this.mapRawRowToColumn(
                "number",
                this.rows,
                this.columns?.find(_ => _),
            ).map(([name, value]) => ({ name, value, unit: this.query.unit }));
        }

        if (this.options.partitionBy) {
            return this.points.map(_ => ({
                name: String(_.x),
                value: _.y || 0,
                unit: this.query.sqlQuery
                    ? this.query.unit || _.UNIT
                    : _.UNIT || this.query.unit,
            }));
        }

        return this.points.map(_ => ({
            name: String(_.GROUP_NAME || _.x),
            value: _.y || 0,
            unit: this.query.sqlQuery
                ? this.query.unit || _.UNIT
                : _.UNIT || this.query.unit,
        }));
    };

    availableXOptions = (columns: ISqlColumn[]): string[] => {
        return (
            columns
                .filter(
                    col =>
                        col.dataType === "timestamp" ||
                        col.name.toLowerCase() === "time" ||
                        col.dataType === "string",
                )
                .map(_ => _.name) || []
        );
    };

    availableYOptions = (columns: ISqlColumn[]): string[] => {
        return (
            columns.filter(col => col.dataType === "number").map(_ => _.name) ||
            []
        );
    };

    availablePartitionOptions = (columns: ISqlColumn[]): string[] => {
        return (
            columns.filter(col => col.dataType === "string").map(_ => _.name) ||
            []
        );
    };

    getRecommendedChartOptions = (columns: ISqlColumn[]): IChartOptions => {
        const xAxis =
            columns.find(col => col.dataType === "timestamp") ||
            columns.find(col => col.name.toLowerCase() === "time") ||
            columns.find(col => col.name.toLowerCase() === "type") ||
            columns.find(
                col =>
                    col.dataType === "string" &&
                    !["label", "key", "unit", "type"].includes(
                        col.name.toLowerCase(),
                    ),
            ) ||
            columns.find(col => col.dataType === "string") ||
            columns.find(_ => _);

        const yAxis = columns
            .filter(
                col =>
                    col.dataType === "number" &&
                    col.name.toLowerCase() !== "time",
            )
            .map(_ => _.name);

        const result = {
            xAxis: defined(xAxis),
            yAxis,
        };

        return result;
    };
}

const deviceIdToDeviceName = (
    deviceId: string | null | undefined,
    label: string,
) => {
    try {
        return deviceId
            ? (deviceStore.get(deviceId) || undefined)?.name || label
            : label;
    } catch {
        return label;
    }
};

export const canSplitByDevice = (
    query: ISqlQuery,
    options: IChartOptions,
): boolean =>
    (query.deviceIds &&
        query.deviceIds.length > 1 &&
        query.deviceIds.length < 8 &&
        options.yAxis.length < 2 &&
        query.groupByDevice) ||
    false;

const toGranularity = (
    level: AnalyticsAggregateLevel,
): [Granularity, number] => {
    switch (level) {
        case "hour":
            return ["minute", 60];
        case "day":
            return ["d", 1];
        case "date":
            return ["d", 1];
        case "week":
            return ["d", 1];
        case "month":
            return ["months", 1];
        case "year":
            return ["years", 1];
        default:
            return ["d", 1];
    }
};

const stringFormat = (level: AnalyticsAggregateLevel): string => {
    switch (level) {
        case "hour":
            return "h:00 a";
        case "day":
            return "EEEE";
        case "date":
            return "MM-dd-YYY";
        case "week":
            return "MM-dd-YYY";
        case "month":
            return "MMM-YYY";
        case "year":
            return "yyyy";
        default:
            return "MM-dd-YYY";
    }
};

const formatTicks = (
    date: string,
    level: AnalyticsAggregateLevel | undefined,
): string => {
    if (!level) {
        return date;
    }
    const newDate = (
        level === "month" || level === "year"
            ? addDays(new Date(date), 1)
            : new Date(date)
    ).toISOString();
    return formatDateString(parseISO(newDate), stringFormat(level));
};

export const toOriginId = (
    type?: string,
    name?: string,
    tableName?: string,
): string => {
    return [type, name, tableName].filter(_ => _).join("-");
};

export const toTableColumnId = (column: ITableColumn): string => {
    if (column.type === "stream") {
        return toOriginId(
            (column as IStreamColumn).streamType,
            (column as IStreamColumn).streamName,
            undefined,
        );
    } else if (column.type === "task-report") {
        return toOriginId(
            undefined,
            (column as ITaskReportColumn).columnName,
            column.tableName,
        );
    } else if (column.type === "event") {
        return toOriginId(
            (column as IEventColumn).eventType,
            undefined,
            column.tableName,
        );
    }
    return "";
};
